"use client";

import { BetaDataTableCell as Cell } from "@zapier/design-system";
import {
  DateCell,
  KebabMenuItem,
  LocationCell,
  OwnerCell,
  Row,
  ServiceIconsWithTooltip,
  TitleCell,
  useRouterParams,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import {
  AssetTypes,
  getAssetIcon,
  toTitleCase,
  getBuildAssetUrl,
  isVirtualFolderNotDefault,
  DEFAULT_FOLDER_ID,
  FOLDER_TRASH_ID,
} from "@zapier/utils";
import {
  convertToAssetId,
  extractAssetOwnerId,
  useSplitTreatments,
} from "@zapier/client";
import { IconName } from "@zapier/zinnia-icons";
import styles from "../AssetTable.module.css";
import { Dateable } from "@zapier/date-am";
import { useTrashRowMenuItems } from "./useTrashRowMenuItems";

const UNTYPED_ASSET_TYPE = "item";

interface Props {
  allowSelection?: boolean;
  appIds?: string[];
  apps?: Record<string, any>;
  asset: any;
  /** Singular, display-cased type name. Defaults to one derived from the asset type. */
  assetTypeLabel?: string;
  disabledShareMessage?: string;
  folderZapTableRow?: boolean;
  folderId?: string;
  /** Overrides the computed value when a row must reserve the checkbox column without offering a checkbox. */
  hiddenSelection?: boolean;
  icon?: IconName;
  isUntitled: boolean;
  lastRunAt?: Dateable | null;
  menuItems?: KebabMenuItem[];
  onClickShare?: () => void;
  onOpenMenu?: () => void;
  showFavoriteButton?: boolean;
  showLastRun?: boolean;
  showLocationColumn?: boolean;
  title: string;
  updatedAt: Dateable | undefined;
  statusCell?: React.ReactNode;
  /** Overrides the url built from the asset type and id. */
  url?: string;
}

/**
 * Presentational row. Receives the already-resolved kebab menu items so the
 * trash-only menu hooks never mount for non-trash rows.
 */
const AssetRowView = ({
  allowSelection,
  appIds,
  apps,
  asset,
  assetTypeLabel,
  disabledShareMessage,
  folderZapTableRow,
  folderId,
  hiddenSelection,
  icon,
  isUntitled,
  lastRunAt,
  onClickShare,
  onOpenMenu,
  rowMenuItems,
  showFavoriteButton = true,
  showLastRun = false,
  showLocationColumn = true,
  title,
  updatedAt,
  statusCell,
  url,
}: Props & { rowMenuItems: KebabMenuItem[] }) => {
  const { search } = useRouterParams();
  const t = useTranslations("last-run");
  // Entries can arrive with no type, which would render labels reading "undefined".
  const assetType = asset.type || UNTYPED_ASSET_TYPE;
  const isTrashView = folderId === FOLDER_TRASH_ID;
  const assetId = convertToAssetId(assetType as AssetTypes, asset.id);
  const typeLabel = assetTypeLabel ?? toTitleCase(assetType);
  // The trash index doesn't carry the previous folder's owner, so a Personal
  // folder (shared id across every member) can't be resolved to the right one.
  const isPreviousFolderLinkable =
    !!asset.folder?.id && asset.folder.id !== DEFAULT_FOLDER_ID;

  return (
    <Row
      hiddenSelection={
        hiddenSelection ??
        (!!folderId && !isVirtualFolderNotDefault(folderId) && !allowSelection)
      }
      allowsSelection={allowSelection}
      id={assetId}
      isTrashed={!!asset.deletedAt}
      onOpenMenu={onOpenMenu}
      menuItems={rowMenuItems}
      assetName={title}
      kebabMenuLabel={`${typeLabel} Actions`}
      kebabMenuAriaLabel={`${typeLabel} Actions for ${title}`}
      showOptions={true}
      showFavoriteButton={showFavoriteButton}
      onClickShare={onClickShare}
      disabledShareMessage={disabledShareMessage}
      assetType={assetType}
      kebabMenuWhiteSpace={true}
    >
      <TitleCell
        icon={icon || getAssetIcon(asset.type) || "miscBoltAltFill"}
        titleAttribute={typeLabel}
        assetTypeTooltip={assetTypeLabel}
        isUntitled={isUntitled}
        title={title}
        url={
          asset.deletedAt
            ? undefined
            : (url ?? getBuildAssetUrl(asset.type, asset.id))
        }
        id={asset.id}
        folderZapTableRow={folderZapTableRow}
        searchQuery={search}
        assetType={assetType}
      />
      {!isTrashView && (
        <>
          <Cell className={styles.statusColumn}>{statusCell}</Cell>
          <Cell className={styles.appsColumn}>
            <ServiceIconsWithTooltip zap={asset} apps={apps} appIds={appIds} />
          </Cell>
        </>
      )}
      {showLocationColumn && (
        <LocationCell
          folderId={asset.folder?.id}
          folderName={asset.folder?.name}
          ownerId={asset.folder?.ownerId}
          // A trashed asset's previous folder is a destination only if the index gave us an id.
          isLinked={isTrashView ? isPreviousFolderLinkable : true}
        />
      )}
      <DateCell
        datetime={
          isTrashView && asset.deletedAt ? new Date(asset.deletedAt) : updatedAt
        }
      />
      {showLastRun && (
        <DateCell
          datetime={lastRunAt}
          emptyLabel={
            asset.type === AssetTypes.Zap ? t("empty-label") : undefined
          }
        />
      )}
      <OwnerCell
        name={asset.owner?.fullName}
        photoUrl={asset.owner?.photoUrl}
      />
    </Row>
  );
};

/**
 * Trash rows get the restore / delete-forever kebab, which subscribes the row
 * to restore + trash mutations and the current user. Isolated in its own
 * component so only trash rows mount those hooks, not every row in the table.
 */
const TrashAssetRow = (props: Props) => {
  const assetType = props.asset.type || UNTYPED_ASSET_TYPE;
  const assetId = convertToAssetId(assetType as AssetTypes, props.asset.id);
  const rowMenuItems = useTrashRowMenuItems({
    assetId,
    folderId: props.folderId,
    assetName: props.title,
    folder: props.asset.folder,
    owner: { id: extractAssetOwnerId(props.asset) },
  });

  return <AssetRowView {...props} rowMenuItems={rowMenuItems} />;
};

const AssetRow = (props: Props) => {
  const isTrashView = props.folderId === FOLDER_TRASH_ID;
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();

  if (isTrashView && isZapAndCanvasUnifiedTrashEnabled) {
    return <TrashAssetRow {...props} />;
  }

  return <AssetRowView {...props} rowMenuItems={props.menuItems || []} />;
};

export default AssetRow;
